import React, { useState, useEffect, useMemo } from 'react';
import clinicService from '../../../../../services/clinic.service';
import { useParams } from 'react-router-dom';
import Loading from '../../../../common/Loading';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend,
    ArcElement,
    PointElement,
    LineElement,
    LineController,
} from 'chart.js';
import { Bar, Pie } from 'react-chartjs-2';
ChartJS.register(
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend,
    ArcElement,
    PointElement,
    LineElement,
    LineController
);

const STATS_CHART_PALETTE = {
    blue: { fill: 'rgba(65, 84, 241, 0.42)', border: '#4154f1', hover: 'rgba(65, 84, 241, 0.62)' },
    cyan: { fill: 'rgba(13, 202, 240, 0.42)', border: '#0aa2c0', hover: 'rgba(13, 202, 240, 0.62)' },
    green: { fill: 'rgba(25, 135, 84, 0.42)', border: '#198754', hover: 'rgba(25, 135, 84, 0.62)' },
    orange: { fill: 'rgba(253, 126, 20, 0.42)', border: '#e8590c', hover: 'rgba(253, 126, 20, 0.62)' },
    gray: { fill: 'rgba(108, 117, 125, 0.38)', border: '#6c757d', hover: 'rgba(108, 117, 125, 0.58)' },
    purple: { fill: 'rgba(111, 66, 193, 0.42)', border: '#6f42c1', hover: 'rgba(111, 66, 193, 0.62)' },
};

const paletteColors = (...keys) => keys.map((key) => STATS_CHART_PALETTE[key]);

const formatNumber = (num) => {
    if (!num) return '0';
    if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
    if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
    return num.toLocaleString();
};

const formatNumberForDisplay = (num) => {
    if (!num) return '0';
    return num.toLocaleString();
};

const calculatePercentage = (value, total) => (total > 0 ? (value / total) * 100 : 0);

const SummaryCard = ({ variant, label, value, sub, icon }) => (
    <div className={`clinic-stats-stat clinic-stats-stat--${variant}`}>
        <div>
            <div className="clinic-stats-stat__label">{label}</div>
            <div className="clinic-stats-stat__value">{value}</div>
            <div className="clinic-stats-stat__desc">{sub}</div>
        </div>
        <span className="clinic-stats-stat__icon">
            <i className={`bi ${icon}`}></i>
        </span>
    </div>
);

const ProgressItem = ({ icon, label, value, total, fillClass, toneClass }) => {
    const pct = calculatePercentage(value, total);
    return (
        <div className={`clinic-stats-detail-card ${toneClass || ''}`}>
            <div className="clinic-stats-detail-card__name">
                <i className={`bi ${icon}`}></i>
                <span>{label}</span>
            </div>
            <div className={`clinic-stats-detail-card__value ${value ? '' : 'clinic-stats-detail-card__value--zero'}`}>
                {formatNumberForDisplay(value)}
            </div>
            <div className="clinic-stats-detail-card__meta">
                {formatNumberForDisplay(value)} of {formatNumberForDisplay(total)} ({pct.toFixed(1)}%)
            </div>
            <div className="clinic-stats-progress-item__bar">
                <div className={`clinic-stats-progress-item__fill ${fillClass}`} style={{ width: `${pct}%` }} role="progressbar" aria-valuenow={value} aria-valuemin={0} aria-valuemax={total}></div>
            </div>
        </div>
    );
};

const AgeTile = ({ variant, label, value, sub, icon }) => (
    <div className={`clinic-stats-age-tile clinic-stats-age-tile--${variant}`}>
        <div className="clinic-stats-age-tile__icon">
            <i className={`bi ${icon}`}></i>
        </div>
        <div className="clinic-stats-age-tile__label">{label}</div>
        <p className="clinic-stats-age-tile__value">{value}</p>
        <small className="clinic-stats-age-tile__sub">{sub}</small>
    </div>
);

function StatsTab() {
    const [clinicStats, setClinicStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const { clinicId } = useParams();

    useEffect(() => {
        setLoading(true);
        setError(null);
        clinicService
            .getPatientStats(clinicId)
            .then((res) => setClinicStats(res.data))
            .catch(() => {
                setError('Failed to load patient statistics.');
                setClinicStats(null);
            })
            .finally(() => setLoading(false));
    }, [clinicId]);

    const totalPatients = clinicStats?.patient?.totalPatient || 0;

    const ageGroupChartData = useMemo(() => {
        const colors = paletteColors('blue', 'cyan', 'green', 'orange', 'gray');
        return {
            labels: ['Children', 'Teenager', 'Adult', 'Senior Citizen', 'Other'],
            datasets: [
                {
                    label: 'Patients',
                    data: [
                        clinicStats?.ageGroup?.children || 0,
                        clinicStats?.ageGroup?.teenager || 0,
                        clinicStats?.ageGroup?.adult || 0,
                        clinicStats?.ageGroup?.seniorCitizen || 0,
                        clinicStats?.ageGroup?.other || 0,
                    ],
                    backgroundColor: colors.map((color) => color.fill),
                    borderColor: colors.map((color) => color.border),
                    hoverBackgroundColor: colors.map((color) => color.hover),
                    borderWidth: 2,
                    borderRadius: 8,
                    borderSkipped: false,
                },
            ],
        };
    }, [clinicStats]);

    const commPrefChartData = useMemo(() => {
        const colors = paletteColors('blue', 'green', 'orange');
        return {
            labels: ['Email', 'Cell Phone', 'Home Phone'],
            datasets: [
                {
                    data: [
                        clinicStats?.commPref?.patientWithEmail || 0,
                        clinicStats?.commPref?.patientWithCellPhone || 0,
                        clinicStats?.commPref?.patientWithHomePhone || 0,
                    ],
                    backgroundColor: colors.map((color) => color.fill),
                    borderColor: colors.map((color) => color.border),
                    hoverBackgroundColor: colors.map((color) => color.hover),
                    borderWidth: 2,
                },
            ],
        };
    }, [clinicStats]);

    const patientStatusData = useMemo(() => {
        const colors = paletteColors('green', 'gray');
        return {
            labels: ['Active', 'Inactive'],
            datasets: [
                {
                    data: [clinicStats?.patient?.activePatient || 0, clinicStats?.patient?.inactivePatient || 0],
                    backgroundColor: colors.map((color) => color.fill),
                    borderColor: colors.map((color) => color.border),
                    hoverBackgroundColor: colors.map((color) => color.hover),
                    borderWidth: 2,
                },
            ],
        };
    }, [clinicStats]);

    const barChartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: { display: false },
            tooltip: {
                backgroundColor: 'rgba(255, 255, 255, 0.96)',
                titleColor: '#012970',
                bodyColor: '#495057',
                borderColor: 'rgba(1, 41, 112, 0.12)',
                borderWidth: 1,
                padding: 10,
                callbacks: {
                    label: (context) => `Patients: ${formatNumberForDisplay(context.raw)}`,
                },
            },
        },
        scales: {
            y: {
                beginAtZero: true,
                ticks: {
                    color: '#6c757d',
                    callback: (value) => formatNumber(value),
                },
                grid: { color: 'rgba(1, 41, 112, 0.08)' },
                border: { display: false },
            },
            x: {
                ticks: { color: '#495057' },
                grid: { display: false },
                border: { display: false },
            },
        },
    };

    const pieChartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                position: 'bottom',
                labels: {
                    color: '#495057',
                    boxWidth: 12,
                    boxHeight: 12,
                    padding: 14,
                    useBorderRadius: true,
                    borderRadius: 4,
                },
            },
            tooltip: {
                backgroundColor: 'rgba(255, 255, 255, 0.96)',
                titleColor: '#012970',
                bodyColor: '#495057',
                borderColor: 'rgba(1, 41, 112, 0.12)',
                borderWidth: 1,
                padding: 10,
                callbacks: {
                    label: (context) => {
                        const value = context.raw || 0;
                        const total = context.dataset.data.reduce((a, b) => a + b, 0);
                        const percentage = total > 0 ? ((value / total) * 100).toFixed(1) : 0;
                        return `${context.label}: ${formatNumberForDisplay(value)} (${percentage}%)`;
                    },
                },
            },
        },
    };

    if (loading) {
        return <Loading />;
    }

    return (
        <div className="clinic-stats-panel">
            <div className="d-flex align-items-center justify-content-between gap-2 mb-3">
                <div>
                    <h5 className="clinic-stats__title mb-1">
                        <i className="bi bi-bar-chart-fill text-primary me-1"></i>
                        Patient Stats
                    </h5>
                    <p className="text-muted small mb-0">Patient demographics, status, and communication breakdown for this clinic.</p>
                </div>
            </div>

            {error && (
                <div className="alert alert-warning border-0 shadow-sm mb-3">
                    <i className="bi bi-exclamation-triangle me-2"></i>
                    {error}
                </div>
            )}

            {!clinicStats && !error ? (
                <div className="clinic-stats-empty">
                    <i className="bi bi-bar-chart"></i>
                    <h5>No patient statistics available</h5>
                    <p className="mb-0">Statistics will appear here once patient data is synced for this clinic.</p>
                </div>
            ) : (
                clinicStats && (
                    <>
                        <div className="clinic-stats-overview-grid">
                            <SummaryCard
                                variant="total"
                                label="Total Patients"
                                value={formatNumber(totalPatients)}
                                sub={`${formatNumberForDisplay(totalPatients)} total`}
                                icon="bi-people-fill"
                            />
                            <SummaryCard
                                variant="active"
                                label="Active Patients"
                                value={formatNumber(clinicStats.patient?.activePatient || 0)}
                                sub={`${formatNumberForDisplay(clinicStats.patient?.activePatient || 0)} active`}
                                icon="bi-person-check-fill"
                            />
                            <SummaryCard
                                variant="inactive"
                                label="Inactive Patients"
                                value={formatNumber(clinicStats.patient?.inactivePatient || 0)}
                                sub={`${formatNumberForDisplay(clinicStats.patient?.inactivePatient || 0)} inactive`}
                                icon="bi-person-dash-fill"
                            />
                        </div>

                        <div className="clinic-stats-charts-row">
                            <section className="clinic-stats-section clinic-stats-section--comm">
                                <div className="clinic-stats-section__header">
                                    <span className="clinic-stats-section__icon">
                                        <i className="bi bi-chat-dots"></i>
                                    </span>
                                    <div>
                                        <div>Communication Preferences</div>
                                        <small className="text-muted fw-normal">Email, cell, and home phone coverage</small>
                                    </div>
                                </div>
                                <div className="clinic-stats-chart-wrap">
                                    <Pie data={commPrefChartData} options={pieChartOptions} />
                                </div>
                            </section>

                            <section className="clinic-stats-section clinic-stats-section--status">
                                <div className="clinic-stats-section__header">
                                    <span className="clinic-stats-section__icon">
                                        <i className="bi bi-pie-chart"></i>
                                    </span>
                                    <div>
                                        <div>Patient Status</div>
                                        <small className="text-muted fw-normal">Active vs inactive patients</small>
                                    </div>
                                </div>
                                <div className="clinic-stats-chart-wrap">
                                    <Pie data={patientStatusData} options={pieChartOptions} />
                                </div>
                            </section>
                        </div>

                        <section className="clinic-stats-section clinic-stats-section--breakdown">
                            <div className="clinic-stats-section__header">
                                <span className="clinic-stats-section__icon">
                                    <i className="bi bi-envelope-paper"></i>
                                </span>
                                <div>
                                    <div>Communication Breakdown</div>
                                    <small className="text-muted fw-normal">Contact method availability across all patients</small>
                                </div>
                                <span className="clinic-stats-section__count">4 metrics</span>
                            </div>
                            <div className="clinic-stats-detail-grid">
                                <ProgressItem
                                    icon="bi-envelope"
                                    label="Email"
                                    value={clinicStats.commPref?.patientWithEmail || 0}
                                    total={totalPatients}
                                    fillClass="clinic-stats-progress-item__fill--email"
                                    toneClass="clinic-stats-detail-card--email"
                                />
                                <ProgressItem
                                    icon="bi-phone"
                                    label="Cell Phone"
                                    value={clinicStats.commPref?.patientWithCellPhone || 0}
                                    total={totalPatients}
                                    fillClass="clinic-stats-progress-item__fill--cell"
                                    toneClass="clinic-stats-detail-card--cell"
                                />
                                <ProgressItem
                                    icon="bi-telephone"
                                    label="Home Phone"
                                    value={clinicStats.commPref?.patientWithHomePhone || 0}
                                    total={totalPatients}
                                    fillClass="clinic-stats-progress-item__fill--home"
                                    toneClass="clinic-stats-detail-card--home"
                                />
                                <ProgressItem
                                    icon="bi-send"
                                    label="Welcome Sent"
                                    value={clinicStats.commPref?.welcomeSent || 0}
                                    total={totalPatients}
                                    fillClass="clinic-stats-progress-item__fill--welcome"
                                    toneClass="clinic-stats-detail-card--welcome"
                                />
                            </div>
                        </section>

                        <section className="clinic-stats-section clinic-stats-section--age-chart">
                            <div className="clinic-stats-section__header">
                                <span className="clinic-stats-section__icon">
                                    <i className="bi bi-bar-chart-steps"></i>
                                </span>
                                <div>
                                    <div>Age Group Chart</div>
                                    <small className="text-muted fw-normal">Patient distribution by age category</small>
                                </div>
                            </div>
                            <div className="clinic-stats-chart-wrap clinic-stats-chart-wrap--bar">
                                <Bar data={ageGroupChartData} options={barChartOptions} />
                            </div>
                        </section>

                        <section className="clinic-stats-section clinic-stats-section--age">
                            <div className="clinic-stats-section__header">
                                <span className="clinic-stats-section__icon">
                                    <i className="bi bi-people"></i>
                                </span>
                                <div>
                                    <div>Age Group Breakdown</div>
                                    <small className="text-muted fw-normal">Counts by demographic segment</small>
                                </div>
                                <span className="clinic-stats-section__count">6 groups</span>
                            </div>
                            <div className="clinic-stats-age-grid">
                                    <AgeTile
                                        variant="children"
                                        label="Children"
                                        value={formatNumber(clinicStats.ageGroup?.children || 0)}
                                        sub={formatNumberForDisplay(clinicStats.ageGroup?.children || 0)}
                                        icon="bi-emoji-smile-fill"
                                    />
                                    <AgeTile
                                        variant="teenager"
                                        label="Teenager"
                                        value={formatNumber(clinicStats.ageGroup?.teenager || 0)}
                                        sub={formatNumberForDisplay(clinicStats.ageGroup?.teenager || 0)}
                                        icon="bi-emoji-laughing-fill"
                                    />
                                    <AgeTile
                                        variant="adult"
                                        label="Adult"
                                        value={formatNumber(clinicStats.ageGroup?.adult || 0)}
                                        sub={formatNumberForDisplay(clinicStats.ageGroup?.adult || 0)}
                                        icon="bi-person-fill"
                                    />
                                    <AgeTile
                                        variant="senior"
                                        label="Senior"
                                        value={formatNumber(clinicStats.ageGroup?.seniorCitizen || 0)}
                                        sub={formatNumberForDisplay(clinicStats.ageGroup?.seniorCitizen || 0)}
                                        icon="bi-person-badge-fill"
                                    />
                                    <AgeTile
                                        variant="other"
                                        label="Other"
                                        value={formatNumber(clinicStats.ageGroup?.other || 0)}
                                        sub={formatNumberForDisplay(clinicStats.ageGroup?.other || 0)}
                                        icon="bi-question-circle-fill"
                                    />
                                    <AgeTile
                                        variant="total"
                                        label="Total"
                                        value={formatNumber(totalPatients)}
                                        sub={formatNumberForDisplay(totalPatients)}
                                        icon="bi-pie-chart-fill"
                                    />
                                </div>
                            </section>
                    </>
                )
            )}
        </div>
    );
}

export default StatsTab;
